import React from 'react';
import { FontIcon, Stack, TooltipHost } from 'office-ui-fabric-react';
import PropTypes from 'prop-types';
import DetailsList from '../DetailsList';
import styles from './RolePermissionGrid.style';
import { can } from '../../utils/auth';

const RolePermissionGrid = ({ roles }) => {
  const roleColumn = [
    {
      key: 'role',
      name: 'Role\\Permission',
      fieldName: 'name',
      isResizable: false,
      minWidth: 150,
      maxWidth: 150,
      onRender: role => {
        return <span>{role.name}</span>;
      },
    },
  ];

  const permissionSet = new Set();
  roles.forEach(role => {
    Object.entries(role.permissions).forEach(([permissionName]) => {
      permissionSet.add(permissionName);
    });
  });
  const permissions = [...permissionSet];

  const getPermissionIcon = permission => {
    if (permission === true)
      return (
        <FontIcon
          data-testid="permission-true"
          aria-label="CheckMark"
          iconName="CheckMark"
          className={styles.iconClass}
        />
      );
    if (permission === false)
      return (
        <FontIcon
          data-testid="permission-false"
          aria-label="Cancel"
          iconName="Cancel"
          className={styles.iconClass}
        />
      );
    return <span> </span>;
  };

  const getPermissionTooltipContent = permission => {
    if (permission) return 'Enabled';
    if (permission === false) return 'Disabled';
    return 'Not Enabled';
  };

  const getColumnWidth = permission => {
    if (permission.length < 18) {
      return 120;
    }
    if (permission.length < 35) {
      return 200;
    }
    return 300;
  };

  const permissionColumns = permissions.map(permission => {
    const colWidth = getColumnWidth(permission);
    return {
      key: permission,
      name: permission,
      fieldName: permission,
      headerClassName: styles.permissionsHeader,
      isResizable: false,
      minWidth: colWidth,
      maxWidth: colWidth,
      onRender: role => {
        const permissionVal = role.permissions[permission];
        const tooltipContent = getPermissionTooltipContent(permissionVal);
        return (
          <TooltipHost
            aria-label={tooltipContent}
            content={tooltipContent}
            styles={{
              root: {
                display: 'flex',
              },
            }}
          >
            {getPermissionIcon(permissionVal)}
          </TooltipHost>
        );
      },
    };
  });

  return (
    <div data-testid="role-permission-grid">
      <Stack horizontal>
        <Stack.Item className={styles.roleStack}>
          <DetailsList
            items={roles.map(role => ({
              ...role,
              key: role.slug,
              link: `/permissions/roles/${role.slug}`,
              dataAutomationId: `role-data-${role.slug}`,
            }))}
            columns={roleColumn}
            disableRowLink={!can('manageRoles')}
          />
        </Stack.Item>
        <Stack.Item className={styles.permissionStack}>
          <DetailsList
            items={roles.map(role => ({
              ...role,
              key: `permission-${role.slug}`,
              link: `/permissions/roles/${role.slug}`,
              dataAutomationId: `permission-data-${role.slug}`,
            }))}
            columns={permissionColumns}
            disableRowLink={!can('manageRoles')}
          />
        </Stack.Item>
      </Stack>
    </div>
  );
};

RolePermissionGrid.propTypes = {
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      permissions: PropTypes.object,
    }),
  ).isRequired,
};

export default RolePermissionGrid;
